import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AuthenticatedImage from "components/AuthenticatedImage";
import { galleryPhotoUrl, isAuthenticatedAlbum, downloadPhotos } from "api/gallery";
import { MONO } from "components/bento/bentoTokens";
import { fmtDay, fmtTime, statusChipColor } from "./parts";

// Full-screen photo viewer with prev/next + keyboard arrows and a metadata
// side panel. `photos` is the flat list the user was browsing; `meta(photo)`
// returns [{ k, v }] rows for the side panel. `downloadBaseName(photo)` names
// saved files (e.g. tracking number / receipt id).
const PhotoLightbox = ({
  open,
  onClose,
  album,
  photos,
  index,
  onIndexChange,
  meta,
  downloadBaseName,
  itemPhotos, // optional: all photos of the current photo's parent item (for "download all")
}) => {
  const { t, i18n } = useTranslation();
  const [saving, setSaving] = useState(false);
  const photo = photos[index];

  const prev = useCallback(
    () => onIndexChange((index - 1 + photos.length) % photos.length),
    [index, photos.length, onIndexChange]
  );
  const next = useCallback(
    () => onIndexChange((index + 1) % photos.length),
    [index, photos.length, onIndexChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prev, next]);

  if (!photo) return null;
  const url = galleryPhotoUrl(album, photo);
  const siblings = itemPhotos && itemPhotos.length > 1 ? itemPhotos : null;

  const save = async (list) => {
    setSaving(true);
    const saved = await downloadPhotos(album, list, downloadBaseName(photo));
    setSaving(false);
    if (saved > 0) toast.success(t("gallery.savedPhotos", { count: saved }));
    else toast.error(t("gallery.downloadFailed"));
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose} PaperProps={{ sx: { backgroundColor: "rgba(10,9,18,.97)" } }}>
      {/* top bar */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "12px 20px" }}>
        <Typography noWrap sx={{ fontFamily: MONO, fontSize: 13, color: "rgba(255,255,255,.85)" }}>
          {(photo.path || photo.filename || "").split("/").pop()}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,.5)", fontVariantNumeric: "tabular-nums" }}>
          {index + 1} / {photos.length}
        </Typography>
        <IconButton onClick={onClose} sx={{ ml: "auto", color: "#fff" }} aria-label={t("common.close")}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        <IconButton onClick={prev} sx={{ alignSelf: "center", color: "rgba(255,255,255,.6)", mx: 0.5 }} aria-label="previous">
          <ChevronLeftIcon fontSize="large" />
        </IconButton>

        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: 0, p: 1 }}>
          {isAuthenticatedAlbum(album) ? (
            <AuthenticatedImage
              src={url}
              alt="photo"
              style={{ maxWidth: "100%", maxHeight: "82vh", objectFit: "contain", borderRadius: 10 }}
            />
          ) : (
            <Box
              component="img"
              src={url}
              alt="photo"
              sx={{ maxWidth: "100%", maxHeight: "82vh", objectFit: "contain", borderRadius: "10px" }}
            />
          )}
        </Box>

        <IconButton onClick={next} sx={{ alignSelf: "center", color: "rgba(255,255,255,.6)", mx: 0.5 }} aria-label="next">
          <ChevronRightIcon fontSize="large" />
        </IconButton>

        {/* side panel */}
        <Box
          sx={{
            width: 280,
            flex: "none",
            display: { xs: "none", md: "block" },
            backgroundColor: "#17151f",
            borderLeft: "1px solid rgba(255,255,255,.08)",
            p: 2.5,
            overflowY: "auto",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#fff", mb: 2 }}>
            {t("gallery.photoDetails")}
          </Typography>
          {photo.status && (
            <Box sx={{ mb: 1.6 }}>
              <Chip size="small" label={photo.status} color={statusChipColor(photo.status)} />
            </Box>
          )}
          {meta(photo).map(({ k, v, onClick }) =>
            v ? (
              <Box key={k} sx={{ mb: 1.4 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.4)" }}>
                  {k}
                </Typography>
                {onClick ? (
                  <Link
                    component="button"
                    onClick={onClick}
                    sx={{ fontSize: 13, fontFamily: MONO, color: "#c4aef5", wordBreak: "break-word", textAlign: "left" }}
                  >
                    {v}
                  </Link>
                ) : (
                  <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.88)", wordBreak: "break-word" }}>{v}</Typography>
                )}
              </Box>
            ) : null
          )}
          <Box sx={{ mb: 1.4 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.4)" }}>
              {t("gallery.taken")}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,.88)" }}>
              {fmtDay(photo.timestamp, i18n.language)} · {fmtTime(photo.timestamp)}
            </Typography>
          </Box>
          {photo.lat && photo.lng && (
            <Box sx={{ mb: 1.4 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "rgba(255,255,255,.4)" }}>
                {t("gallery.location")}
              </Typography>
              <Link
                href={`https://www.google.com/maps?q=${photo.lat},${photo.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: 13, color: "#c4aef5" }}
              >
                {photo.lat}, {photo.lng}
              </Link>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2.5 }}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
              disabled={saving}
              onClick={() => save([photo])}
            >
              {t("gallery.downloadPhoto")}
            </Button>
            {siblings && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<DownloadIcon />}
                disabled={saving}
                onClick={() => save(siblings)}
                sx={{ color: "#c4aef5", borderColor: "rgba(196,174,245,.5)" }}
              >
                {t("gallery.downloadAll", { count: siblings.length })}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PhotoLightbox;
